/**
 * Iridium NEXT constellation overlay — live satellite positions and their
 * Aireon (space-based ADS-B) coverage footprints. Sources/layers are always
 * registered (like the approach overlays in layers.ts) so toggling the
 * overlay is a pure visibility flip; `updateSatellites` still skips the
 * (cheap but non-zero) SGP4 propagation work when `visible` is false.
 */
import type { GeoJSONSource, Map as MlMap } from 'maplibre-gl';
import * as satellite from 'satellite.js';
import { circlePolygon } from './geo';
import { apiUrl } from '../api';

export const SATELLITES_SOURCE = 'satellites';
export const SATELLITES_LAYER = 'satellites-layer';
export const SATELLITE_LABELS_LAYER = 'satellite-labels-layer';
export const SATELLITE_FOOTPRINTS_SOURCE = 'satellite-footprints';
export const SATELLITE_FOOTPRINTS_LAYER = 'satellite-footprints-layer';

const EARTH_RADIUS_KM = satellite.constants.earthRadius; // ~6378.135 km (WGS72, matches SGP4's own model)
/** Iridium NEXT operates at ~780 km altitude — footprint radius is fixed rather than recomputed per-satellite from noisy live altitude, since the constellation is nominally circular at one altitude. */
const IRIDIUM_ALTITUDE_KM = 780;
const FOOTPRINT_RADIUS_KM = EARTH_RADIUS_KM * Math.acos(EARTH_RADIUS_KM / (EARTH_RADIUS_KM + IRIDIUM_ALTITUDE_KM));

const empty = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });

interface SatEntry {
  name: string;
  satrec: satellite.SatRec;
}

let satrecs: SatEntry[] = [];
let loadPromise: Promise<void> | null = null;

export function addSatelliteLayers(map: MlMap, opts: { projector: boolean }): void {
  if (!map.getSource(SATELLITE_FOOTPRINTS_SOURCE)) {
    map.addSource(SATELLITE_FOOTPRINTS_SOURCE, { type: 'geojson', data: empty() });
  }
  if (!map.getSource(SATELLITES_SOURCE)) {
    map.addSource(SATELLITES_SOURCE, { type: 'geojson', data: empty() });
  }

  // Footprints (bottom): very subtle so 66 overlapping circles don't wash
  // out the ocean — dashed outline only, near-invisible fill.
  if (!map.getLayer(SATELLITE_FOOTPRINTS_LAYER)) {
    map.addLayer({
      id: SATELLITE_FOOTPRINTS_LAYER,
      type: 'fill',
      source: SATELLITE_FOOTPRINTS_SOURCE,
      layout: { visibility: 'none' },
      paint: {
        'fill-color': opts.projector ? '#5eead4' : '#0d9488',
        'fill-opacity': opts.projector ? 0.05 : 0.04,
      },
    });
    map.addLayer({
      id: `${SATELLITE_FOOTPRINTS_LAYER}-outline`,
      type: 'line',
      source: SATELLITE_FOOTPRINTS_SOURCE,
      layout: { visibility: 'none' },
      paint: {
        'line-color': opts.projector ? '#5eead4' : '#0d9488',
        'line-opacity': opts.projector ? 0.35 : 0.25,
        'line-width': 1,
        'line-dasharray': [2, 3],
      },
    });
  }

  // Satellite points + labels (top).
  if (!map.getLayer(SATELLITES_LAYER)) {
    map.addLayer({
      id: SATELLITES_LAYER,
      type: 'circle',
      source: SATELLITES_SOURCE,
      layout: { visibility: 'none' },
      paint: {
        'circle-radius': opts.projector ? 2.5 : 2,
        'circle-color': opts.projector ? '#5eead4' : '#0f766e',
        'circle-stroke-width': 1,
        'circle-stroke-color': opts.projector ? '#003a3a' : '#ffffff',
      },
    });
    map.addLayer({
      id: SATELLITE_LABELS_LAYER,
      type: 'symbol',
      source: SATELLITES_SOURCE,
      // Labels are only useful once zoomed in enough that 66 of them
      // wouldn't overlap into an unreadable cluster.
      minzoom: opts.projector ? 4 : 5,
      layout: {
        visibility: 'none',
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': opts.projector ? 12 : 10,
        'text-offset': [0, 0.8],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': opts.projector ? '#99f6e4' : '#134e4a',
        'text-halo-color': opts.projector ? '#000000' : '#ffffff',
        'text-halo-width': 1.2,
      },
    });
  }
}

/** Fetch /api/tle once and parse into satrecs, cached module-locally. Safe to call repeatedly — subsequent calls reuse the in-flight/completed load. */
export function loadTles(): Promise<void> {
  loadPromise ??= (async () => {
    const res = await fetch(apiUrl('/api/tle'));
    if (!res.ok) throw new Error(`TLE fetch failed: HTTP ${res.status}`);
    const tles = (await res.json()) as { name: string; line1: string; line2: string }[];
    satrecs = tles.map((t) => ({ name: t.name, satrec: satellite.twoline2satrec(t.line1, t.line2) }));
  })().catch((err) => {
    loadPromise = null; // allow a retry on the next call
    throw err;
  });
  return loadPromise;
}

function setLayersVisible(map: MlMap, visible: boolean): void {
  const layout: 'visible' | 'none' = visible ? 'visible' : 'none';
  for (const id of [
    SATELLITES_LAYER,
    SATELLITE_LABELS_LAYER,
    SATELLITE_FOOTPRINTS_LAYER,
    `${SATELLITE_FOOTPRINTS_LAYER}-outline`,
  ]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', layout);
  }
}

let shown = false;

/**
 * Propagate every satellite to `now` and redraw points + footprints.
 * Satellites move ~7.4 km/s so this must run every tick while visible — kept
 * cheap by only propagating (SGP4, no GeoJSON build) when actually visible,
 * and reusing the already-parsed satrecs from loadTles() rather than
 * re-parsing per frame.
 */
export function updateSatellites(map: MlMap, now: number, visible: boolean): void {
  if (visible !== shown) {
    setLayersVisible(map, visible);
    shown = visible;
  }
  if (!visible) return;

  const ptSrc = map.getSource(SATELLITES_SOURCE) as GeoJSONSource | undefined;
  const fpSrc = map.getSource(SATELLITE_FOOTPRINTS_SOURCE) as GeoJSONSource | undefined;
  if (!ptSrc || !fpSrc || satrecs.length === 0) return;

  const date = new Date(now);
  const gmst = satellite.gstime(date);
  const points: GeoJSON.Feature[] = [];
  const footprints: GeoJSON.Feature[] = [];

  for (const { name, satrec } of satrecs) {
    const pv = satellite.propagate(satrec, date);
    if (!pv || !pv.position) continue; // decayed/invalid element set
    const geo = satellite.eciToGeodetic(pv.position, gmst);
    const lat = satellite.degreesLat(geo.latitude);
    const lon = satellite.degreesLong(geo.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

    points.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lon, lat] },
      properties: { name },
    });
    footprints.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [circlePolygon([lon, lat], FOOTPRINT_RADIUS_KM)] },
      properties: { name },
    });
  }

  ptSrc.setData({ type: 'FeatureCollection', features: points });
  fpSrc.setData({ type: 'FeatureCollection', features: footprints });
}
